import { useEffect, useId, useRef, useState } from 'react';

import { Button, Card } from '@/design-system';
import type { ReportInvoiceChoice, ReportRecipient } from '@/entities/report';

import { reportCopy } from '../copy';
import { useInvoiceChoice, type InvoiceEnclosureSetup } from '../useInvoiceChoice';
import { ClientLanguageField } from './ClientLanguageField';
import { InvoiceEnclosure } from './InvoiceEnclosure';
import { RecipientList } from './RecipientList';

export interface SendConfirmProps {
    recipients: readonly ReportRecipient[];
    busy: boolean;
    /** Factuur meesturen; alleen voor wie facturen mag opstellen en uitgeven. */
    invoice?: InvoiceEnclosureSetup;
    /** De talen waarin de klant het rapport kan krijgen; één taal: geen keuze. */
    languages?: readonly string[];
    /** Versturen; met de factuur als die meegaat, en de taal voor de klant (`undefined`: Nederlands). */
    onConfirm: (invoice: ReportInvoiceChoice | undefined, language?: string) => void;
    onCancel: () => void;
}

/**
 * Bevestiging van "Opnieuw versturen": een goedgekeurd rapport gaat (weer) naar zijn
 * ontvangers, met de link die ze al hebben.
 *
 * Een rapport dat alleen werd vastgesteld gaat hier voor het eerst de deur uit; dan
 * kan de factuur mee, met het eigen bericht, net als bij goedkeuren. Heeft de klant
 * de factuur al gekregen - met een eerdere bezorging, of los - dan zegt het blok dat
 * ze niet nog een keer meegaat.
 */
export function SendConfirm({
    recipients,
    busy,
    invoice,
    languages = ['nl'],
    onConfirm,
    onCancel,
}: SendConfirmProps) {
    const titleId = useId();
    const [language, setLanguage] = useState('nl');
    const confirmRef = useRef<HTMLButtonElement>(null);
    const deliverable = recipients.filter((recipient) => recipient.email !== null);
    const enclosure = useInvoiceChoice(invoice, recipients);

    useEffect(() => {
        confirmRef.current?.focus();
    }, []);

    return (
        <Card
            variant="accent"
            padding="lg"
            role="group"
            aria-labelledby={titleId}
            className="mb-[14px]"
        >
            <h2
                id={titleId}
                className="m-0 mb-2 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {reportCopy.sendTitle}
            </h2>
            <p className="m-0 text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                {reportCopy.sendBody}
            </p>

            <RecipientList recipients={recipients} />

            {deliverable.length > 0 ? (
                <ClientLanguageField
                    languages={languages}
                    value={language}
                    onChange={setLanguage}
                    disabled={busy}
                />
            ) : null}

            {invoice && deliverable.length > 0 ? (
                <InvoiceEnclosure {...enclosure.fields} disabled={busy} />
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2">
                <Button
                    ref={confirmRef}
                    variant="primary"
                    size="md"
                    disabled={busy || deliverable.length === 0}
                    onClick={() =>
                        onConfirm(enclosure.choice, language === 'nl' ? undefined : language)
                    }
                >
                    {reportCopy.sendConfirm(deliverable.length)}
                </Button>
                <Button variant="outline" size="md" disabled={busy} onClick={onCancel}>
                    {reportCopy.confirmCancel}
                </Button>
            </div>
        </Card>
    );
}
